import { LitElement, html, css } from 'lit-element';
import './agent-pane.js';
import { PERSONALITY_NAMES } from './personalities.js';
import { lcmOfSet } from '../utils/math.js';

const DEFAULT_COLS = 6;
const DEFAULT_ROWS = 4;
const DEFAULT_TICK_MS = 120;

class AgentGrid extends LitElement {
  static get properties() {
    return {
      cols: { type: Number },
      rows: { type: Number },
      tickMs: { type: Number, attribute: 'tick-ms' },
      paused: { type: Boolean, reflect: true },
      _alignStrength: { state: true },
      _meta: { state: true },
    };
  }

  constructor() {
    super();
    this.cols = DEFAULT_COLS;
    this.rows = DEFAULT_ROWS;
    this.tickMs = DEFAULT_TICK_MS;
    this.paused = false;
    this._timer = null;
    this._panes = [];
    this._tick = 0;
    this._alignStrength = 0;
    this._meta = { lcmCycle: 0, alignmentsAt: [] };
  }

  static get styles() {
    return css`
      :host {
        position: fixed;
        inset: 0;
        display: grid;
        gap: 1px;
        background: #050816;
      }
      agent-pane {
        cursor: pointer;
      }
      .flash {
        position: fixed;
        inset: 0;
        pointer-events: none;
        background: radial-gradient(
          ellipse at center,
          rgba(255, 255, 255, 0.35),
          rgba(160, 200, 255, 0.0) 60%
        );
        opacity: var(--alignment, 0);
        mix-blend-mode: screen;
        transition: opacity 120ms linear;
      }
      .hud {
        position: fixed;
        bottom: 10px;
        right: 12px;
        font: 11px/1.5 ui-monospace, Menlo, monospace;
        color: rgba(255, 255, 255, 0.7);
        background: rgba(10, 14, 30, 0.72);
        padding: 8px 10px;
        border-radius: 6px;
        pointer-events: none;
        max-width: 360px;
      }
      .hud em {
        color: #9ecbff;
        font-style: normal;
      }
    `;
  }

  firstUpdated() {
    this._applyGrid();
    this._spawnPanes();
    this._start();
  }

  updated(changed) {
    if (changed.has('cols') || changed.has('rows')) {
      this._applyGrid();
      this._spawnPanes();
    }
    if (changed.has('tickMs')) this._restart();
    if (changed.has('paused')) (this.paused ? this._stop() : this._start());
    this.style.setProperty('--alignment', String(this._alignStrength));
  }

  _applyGrid() {
    this.style.gridTemplateColumns = `repeat(${this.cols}, 1fr)`;
    this.style.gridTemplateRows = `repeat(${this.rows}, 1fr)`;
  }

  _spawnPanes() {
    const host = this.renderRoot.querySelector('#cells');
    if (!host) return;
    host.innerHTML = '';
    this._panes = [];
    const total = this.cols * this.rows;
    for (let i = 0; i < total; i += 1) {
      const p = document.createElement('agent-pane');
      p.personality = PERSONALITY_NAMES[i % PERSONALITY_NAMES.length];
      p.initFromIndex(i);
      p.addEventListener('click', () => this._cyclePersonality(p));
      host.appendChild(p);
      this._panes.push(p);
    }
    this._recomputeMeta();
  }

  _cyclePersonality(pane) {
    const idx = PERSONALITY_NAMES.indexOf(pane.personality);
    pane.personality = PERSONALITY_NAMES[(idx + 1) % PERSONALITY_NAMES.length];
    pane.requestUpdate();
    this._recomputeMeta();
  }

  _recomputeMeta() {
    const divisions = this._panes.map((p) => p.division);
    this._meta = {
      lcmCycle: lcmOfSet(divisions.length ? divisions : [1]),
      divisions,
    };
  }

  _stepAll() {
    if (!this._panes.length) return;
    this._tick += 1;
    const snapshots = this._panes.map((p) => p.snapshot());
    let firedCount = 0;
    this._panes.forEach((pane, i) => {
      const r = Math.floor(i / this.cols);
      const c = i % this.cols;
      const neighbors = [];
      for (let dr = -1; dr <= 1; dr += 1) {
        for (let dc = -1; dc <= 1; dc += 1) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (nr < 0 || nr >= this.rows || nc < 0 || nc >= this.cols) continue;
          neighbors.push(snapshots[nr * this.cols + nc]);
        }
      }
      pane.step(neighbors);
      if (pane.phase === 0) firedCount += 1;
      pane.requestUpdate();
    });
    // alignment strength = fraction of panes hitting downbeat this tick
    const ratio = firedCount / this._panes.length;
    // square it so partial overlaps stay dim, only near-total syncs flash
    this._alignStrength = Math.pow(ratio, 3);
    // when most panes fire together, recompute LCM (divisions may have shifted)
    if (this._tick % 32 === 0) this._recomputeMeta();
  }

  _start() {
    this._stop();
    this._timer = setInterval(() => this._stepAll(), this.tickMs);
  }

  _stop() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
  }

  _restart() {
    if (!this.paused) this._start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stop();
  }

  render() {
    const divs = (this._meta && this._meta.divisions) || [];
    const lcmCycle = (this._meta && this._meta.lcmCycle) || 0;
    return html`
      <div id="cells" style="display:contents;"></div>
      <div class="flash"></div>
      <div class="hud">
        ${this.cols}×${this.rows} · tick ${this.tickMs}ms ·
        LCM cycle <em>${lcmCycle.toLocaleString()}</em> ticks
        (${((lcmCycle * this.tickMs) / 1000).toFixed(1)}s)<br />
        divisions: ${divs.join(' ')}<br />
        click pane → cycle personality · space → pause
      </div>
    `;
  }
}

customElements.define('agent-grid', AgentGrid);
